import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import API from "services/lib/api";
import { apiRoute } from "services";
import { Loading } from "components/ui";
import asset from "assets/images";
import { dataQueryStatus } from "utils";
import { getErrorMessage } from "utils/helper";
import ErrorView from "components/ui/ErrorView/ErrorView";
import { saveRoleGroups } from "store/modules/admin/actions";
import "./PermissionsCheckBox.scss";

const { DATAMODE, LOADING, ERROR } = dataQueryStatus;

// The "View ..." permission acts as the gatekeeper for a group; if view is
// not granted, all other permissions in the same group are unreachable.
// We prefer matching by display name ("View …") because some groups contain
// multiple READ_* permissions and only one of them is the conceptual head
// (e.g. Ticket Management → "View agent inbox").
const findViewPermission = (items = []) => {
    const byDisplayName = items.find((item) =>
        item?.displayName?.toLowerCase().startsWith("view")
    );
    if (byDisplayName) return byDisplayName;
    return items.find((item) =>
        item?.permissionCode?.toUpperCase().startsWith("READ_")
    );
};

const PermissionToggle = ({ item, checked, onChange, disabled, isHead }) => (
    <button
        type='button'
        className={`permission-group__item ${
            isHead ? "is-head" : "is-leaf"
        } ${disabled ? "is-disabled" : ""}`}
        onClick={onChange}
        disabled={disabled}
        aria-pressed={checked}>
        {!isHead && (
            <span className='permission-group__tree'>
                <ReactSVG src={asset?.icons?.dropTree} />
            </span>
        )}
        <span className='permission-group__check'>
            <ReactSVG
                src={
                    checked
                        ? asset?.icons?.greenChecker
                        : asset?.icons?.greyUncheck
                }
            />
        </span>
        <span className='permission-group__item-label'>
            {item.displayName}
        </span>
    </button>
);

const PermissionsCheckBox = ({ existingPermissions = [], updatePerms }) => {
    const [errorMsg, setErrorMsg] = useState();
    const [openGroups, setOpenGroups] = useState({});
    const [selected, setSelected] = useState(new Set(existingPermissions));

    const {
        rolesCache: { roleGroups },
    } = useSelector((state) => state.admin);

    const [status, setStatus] = useState(
        roleGroups?.length > 0 ? DATAMODE : LOADING
    );

    const dispatch = useDispatch();

    useEffect(() => {
        const httpRequest = new AbortController();
        if (!roleGroups || roleGroups.length < 1) {
            getAllPermissionGroups(httpRequest);
        } else {
            setStatus(DATAMODE);
        }
        return () => httpRequest.abort();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setSelected(new Set(existingPermissions || []));
    }, [existingPermissions]);

    const getAllPermissionGroups = async (httpRequest) => {
        try {
            if (!roleGroups || roleGroups.length < 1) setStatus(LOADING);

            const res = await API.get(apiRoute.getPermissionGroups, {
                signal: httpRequest?.signal,
            });

            if (res.status === 200) {
                const { data } = res.data;
                dispatch(saveRoleGroups(data));
                setStatus(DATAMODE);
            }
        } catch (err) {
            setStatus(ERROR);
            setErrorMsg(getErrorMessage(err));
        }
    };

    const groupSelectedCount = useMemo(() => {
        const counts = {};
        (roleGroups || []).forEach((group) => {
            counts[group.groupCode] = (group.items || []).reduce(
                (acc, item) =>
                    selected.has(item.permissionCode) ? acc + 1 : acc,
                0
            );
        });
        return counts;
    }, [roleGroups, selected]);

    const toggleGroup = (group) => {
        const { groupCode } = group;
        const willOpen = !openGroups[groupCode];
        setOpenGroups((prev) => ({ ...prev, [groupCode]: willOpen }));

        if (willOpen) {
            const viewItem = findViewPermission(group?.items || []);
            if (viewItem && !selected.has(viewItem.permissionCode)) {
                const next = new Set(selected);
                next.add(viewItem.permissionCode);
                setSelected(next);
                updatePerms(Array.from(next));
            }
        }
    };

    const togglePermission = (group, item) => {
        const next = new Set(selected);
        const viewItem = findViewPermission(group?.items || []);
        const isViewItem =
            !!viewItem && viewItem.permissionCode === item.permissionCode;

        if (next.has(item.permissionCode)) {
            next.delete(item.permissionCode);
            if (isViewItem) {
                (group?.items || []).forEach((other) => {
                    if (other.permissionCode !== item.permissionCode) {
                        next.delete(other.permissionCode);
                    }
                });
            }
        } else {
            next.add(item.permissionCode);
        }
        setSelected(next);
        updatePerms(Array.from(next));
    };

    const renderGroup = (group) => {
        const isOpen = !!openGroups[group.groupCode];
        const items = group.items || [];
        const selectedCount = groupSelectedCount[group.groupCode] || 0;
        const totalCount = items.length;

        const viewItem = findViewPermission(items);
        const dependentItems = viewItem
            ? items.filter(
                  (item) =>
                      item.permissionCode !== viewItem.permissionCode
              )
            : items;
        const viewGranted = viewItem
            ? selected.has(viewItem.permissionCode)
            : true;

        return (
            <div
                key={group.groupCode}
                className={`permission-group ${isOpen ? "is-open" : ""}`}>
                <button
                    type='button'
                    className='permission-group__header'
                    onClick={() => toggleGroup(group)}
                    aria-expanded={isOpen}>
                    <span className='permission-group__chevron'>
                        <ReactSVG src={asset?.icons?.arrowRight} />
                    </span>
                    <span className='permission-group__title'>
                        {group.groupDisplayName}
                    </span>
                    {selectedCount > 0 ? (
                        <span className='permission-group__count'>
                            {selectedCount}/{totalCount}
                        </span>
                    ) : null}
                </button>

                {isOpen ? (
                    <div className='permission-group__body'>
                        {viewItem && (
                            <PermissionToggle
                                item={viewItem}
                                checked={viewGranted}
                                onChange={() =>
                                    togglePermission(group, viewItem)
                                }
                                isHead
                            />
                        )}
                        {dependentItems.map((item) => (
                            <PermissionToggle
                                key={item.permissionCode}
                                item={item}
                                checked={selected.has(item.permissionCode)}
                                disabled={!!viewItem && !viewGranted}
                                onChange={() =>
                                    togglePermission(group, item)
                                }
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderBasedOnStatus = () => {
        switch (status) {
            case LOADING:
                return <Loading />;
            case ERROR:
                return (
                    <ErrorView
                        message={errorMsg}
                        size={40}
                        handleRetry={getAllPermissionGroups}
                    />
                );
            case DATAMODE:
                return (
                    <div className='permission-groups'>
                        {roleGroups?.map(renderGroup)}
                    </div>
                );
            default:
                return "";
        }
    };

    return (
        <div className='permissions-section'>
            <div className='permissions-section__header'>
                <div>
                    <p className='permissions-section__title'>
                        Permission Groups
                    </p>
                    <p className='permissions-section__subtitle'>
                        Expand a group to enable specific permissions.
                    </p>
                </div>
                <span className='permissions-section__selected-tag'>
                    {selected.size} selected
                </span>
            </div>
            {renderBasedOnStatus()}
        </div>
    );
};

export default PermissionsCheckBox;
